import { useCallback, useEffect, useMemo, useState } from 'react';

import checkFeatureFlagByName from 'api/feature-flags/checkFeatureFlagByName';
import { CurrentUserI } from 'api/users/interfaces/Users';

import { analytics } from 'services/analytics';
import { UserRole } from 'types/dashboard';

export const CONTACT_DATA_BANNER_EVENTS = {
	SHOWN: 'contact_data_banner_shown',
	CLICKED: 'contact_data_banner_clicked',
	SAVED: 'contact_data_saved',
	SHOWN_SESSION_KEY: 'contact_data_banner_shown_session',
	SOURCE_SESSION_KEY: 'contact_data_banner_source'
} as const;

export const CONTACT_DATA_BANNER_FEATURE_FLAG = 'contact_data_banner';

export type ContactDataMissingField = 'phone' | 'personalEmail';

type User = CurrentUserI['user'] | null | undefined;

interface UseContactDataBannerResult {
	isVisible: boolean;
	missingFields: ContactDataMissingField[];
	onCtaClick: () => void;
}

export const useContactDataBanner = (user: User, token?: string): UseContactDataBannerResult => {
	// Cualquier empleado B2B con rol "user" (puede tener otros roles encima).
	// Cuarteto admin (roles=['admin'], companyId=null) queda excluido por companyId.
	const hasUserRole = (user?.roles ?? []).includes(UserRole.USER);
	const isUserEligible =
		!!user?.companyId && !user.isFreelance && !user.isIndividual && hasUserRole;

	// Feature flag — default false: si la flag no existe en BD o el fetch falla,
	// el banner no se muestra. Permite activar/desactivar sin redeploy.
	const [flagEnabled, setFlagEnabled] = useState(false);
	const userId = user?.id;
	const companyId = user?.companyId;

	useEffect(() => {
		if (!isUserEligible || !token || !userId || !companyId) {
			setFlagEnabled(false);
			return;
		}
		let cancelled = false;
		checkFeatureFlagByName(token, companyId, userId, CONTACT_DATA_BANNER_FEATURE_FLAG)
			.then((res) => {
				if (cancelled) return;
				setFlagEnabled(res?.success === true && res.data?.isActive === true);
			})
			.catch(() => {
				if (!cancelled) setFlagEnabled(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isUserEligible, token, userId, companyId]);

	const missingPhone = isUserEligible && !user?.phone;
	const missingEmail = isUserEligible && !user?.personalEmail;
	const isVisible = isUserEligible && flagEnabled && (missingPhone || missingEmail);

	const missingFields = useMemo<ContactDataMissingField[]>(() => {
		const fields: ContactDataMissingField[] = [];
		if (missingPhone) fields.push('phone');
		if (missingEmail) fields.push('personalEmail');
		return fields;
	}, [missingPhone, missingEmail]);

	useEffect(() => {
		if (!isVisible) return;
		if (sessionStorage.getItem(CONTACT_DATA_BANNER_EVENTS.SHOWN_SESSION_KEY)) return;

		analytics.track(CONTACT_DATA_BANNER_EVENTS.SHOWN, {
			missing_phone: missingPhone,
			missing_personal_email: missingEmail
		});
		sessionStorage.setItem(CONTACT_DATA_BANNER_EVENTS.SHOWN_SESSION_KEY, 'true');
	}, [isVisible, missingPhone, missingEmail]);

	const onCtaClick = useCallback(() => {
		sessionStorage.setItem(CONTACT_DATA_BANNER_EVENTS.SOURCE_SESSION_KEY, 'banner');
		analytics.track(CONTACT_DATA_BANNER_EVENTS.CLICKED, {
			missing_phone: missingPhone,
			missing_personal_email: missingEmail
		});
	}, [missingPhone, missingEmail]);

	return { isVisible, missingFields, onCtaClick };
};
